/**
 * Hide-for-me (GH-958): the items this device's reader has reported, kept so they stay out of the
 * comment wall, the question list and "Just now" on every poll and reload.
 *
 * Local storage, per event, and every call is wrapped: a private window or a refused storage keeps
 * the set in memory for the life of the page and the page works the same. Nothing is sent
 * anywhere from here.
 */
const HIDDEN_PREFIX = "givehere.report.hidden.v1.";
const DEVICE_KEY = "givehere.report.device.v1";
const DEVICE_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;
const DEVICE_ID_MAX = 64;
/** Enough to outlast any one night's lists; older entries fall off the front. */
const MAX_HIDDEN_PER_EVENT = 500;

/** What storage refused to keep, per event, so a reader's report still hides for the page's life. */
const memoryHidden = new Map<string, readonly string[]>();
let memoryDeviceId: string | null = null;

const storage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export function readHiddenKeys(eventId: string): ReadonlySet<string> {
  const fromMemory = memoryHidden.get(eventId) ?? [];
  try {
    const raw = storage()?.getItem(HIDDEN_PREFIX + eventId);
    if (raw === null || raw === undefined) return new Set(fromMemory);
    const parsed: unknown = JSON.parse(raw);
    const stored = Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === "string")
      : [];
    return new Set([...stored, ...fromMemory]);
  } catch {
    return new Set(fromMemory);
  }
}

/** Returns the new set; the old one is never changed. */
export function addHiddenKey(eventId: string, key: string): ReadonlySet<string> {
  const next = [...new Set([...readHiddenKeys(eventId), key])].slice(-MAX_HIDDEN_PER_EVENT);
  memoryHidden.set(eventId, next);
  try {
    storage()?.setItem(HIDDEN_PREFIX + eventId, JSON.stringify(next));
  } catch {
    // Refused: the in-memory copy serves this page.
  }
  return new Set(next);
}

/** Clears the in-memory copy only. A reload does this; tests use it to simulate one. */
export function forgetInMemoryReports(): void {
  memoryHidden.clear();
  memoryDeviceId = null;
}

const makeDeviceId = (): string => {
  const random =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random()
          .toString(36)
          .slice(2)}`;
  const cleaned = random.replace(/[^A-Za-z0-9_-]/g, "").slice(0, DEVICE_ID_MAX);
  return DEVICE_ID_PATTERN.test(cleaned) ? cleaned : `d${Date.now().toString(36)}xxxxxxxx`;
};

/** A stable random id for this browser. It identifies no one; the server hashes it. */
export function reportDeviceId(): string {
  try {
    const stored = storage()?.getItem(DEVICE_KEY);
    if (stored && DEVICE_ID_PATTERN.test(stored)) return stored;
  } catch {
    // fall through to memory
  }
  if (memoryDeviceId === null) memoryDeviceId = makeDeviceId();
  try {
    storage()?.setItem(DEVICE_KEY, memoryDeviceId);
  } catch {
    // Refused: the in-memory id is stable for this page.
  }
  return memoryDeviceId;
}
